/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { useMemo } from 'react';
import { nanoid } from 'nanoid';
import { useCommerceAPI } from '../contexts';
import {
    Customer,
    CustomerAddress,
    CustomerCredentials,
    CustomerPaymentInstrumentRequest,
    CustomerRegistration,
} from '~/lib/data-contract/salesforce';
import { useEvents } from '~/shared/hooks/useEvents';
import { useFrame } from '~/shared/utils';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useLocation } from 'react-use';
import { setCustomer, useCustomerStore } from './useCustomerStore';

export const AuthTypes = Object.freeze({ GUEST: 'guest', REGISTERED: 'registered' });

export default function useCustomer() {
    const api = useCommerceAPI();
    const { data: frame } = useFrame();
    const { customer } = useCustomerStore((state) => ({
        customer: state.customer,
    }));

    const location = useLocation();

    const { authenticationEvent } = useEvents(frame);
    const { trackAndDisplayError: trackError } = useErrorHandler();

    const mergeBasketToCustomer = async (customer: Customer, isNewlyRegistered?: boolean) => {
        if (!customer.customerId) return;
        if (isNewlyRegistered) return localStorage.removeItem('mergeBasket');
        const mergeBasket = localStorage.getItem('mergeBasket');
        if (mergeBasket !== 'true') return;
        const token = localStorage.getItem('guestToken');
        const basketId = localStorage.getItem('guestBasketId');

        const body = {
            c_guestToken: token,
            c_guestBasketID: basketId,
        };
        const parameters = {
            customerId: customer.customerId,
        };

        try {
            await api.shopperCustomers?.updateCustomer({ parameters, body });
            localStorage.setItem('mergeBasket', 'false');
            return;
        } catch (e) {
            trackError(new Error('Could not merge basket items'), e);
        }
    };

    const self = useMemo(() => {
        return {
            ...customer,

            /**
             * Returns boolean value whether the user data is initialized
             */
            get isInitialized() {
                return !!customer?.customerId;
            },

            /**
             * Returns boolean value whether the customer is of type `registered` or not.
             */
            get isRegistered() {
                return customer?.authType === AuthTypes.REGISTERED;
            },

            /**
             * Returns boolean value whether the customer is of type `guest` or not.
             */
            get isGuest() {
                return customer?.authType === AuthTypes.GUEST;
            },

            /** Returns the customer's saved addresses with the 'preferred' address in the first index */
            get addresses() {
                if (!customer?.addresses) {
                    return undefined;
                }
                const preferredAddressIndex = customer.addresses.findIndex(
                    (addr) => addr.preferred
                );
                if (preferredAddressIndex > -1) {
                    return [
                        customer.addresses[preferredAddressIndex],
                        ...customer.addresses.slice(
                            preferredAddressIndex,
                            preferredAddressIndex + 1
                        ),
                    ];
                }
                return customer.addresses;
            },
            get isPlusMember() {
                return customer?.authType === AuthTypes.REGISTERED && customer?.c_isMemberPlus;
            },
            get isStorePersonnel() {
                return customer?.authType === AuthTypes.REGISTERED && customer?.c_isStorePersonnel;
            },

            get isNotPlusMember() {
                return customer?.authType === AuthTypes.REGISTERED && !customer?.c_isMemberPlus;
            },

            /**
             * Log in customer account.
             *
             * @param {object} credentials
             * @param {string} credentials.password
             * @param {boolean} isNewlyRegistered
             */
            async login(credentials?: CustomerCredentials, isNewlyRegistered?: boolean) {
                const skeletonCustomer = await api.auth?.login(credentials);

                if (skeletonCustomer.authType === 'guest') {
                    setCustomer(skeletonCustomer);
                } else {
                    const customer = await api.shopperCustomers?.getCustomer({
                        parameters: { customerId: skeletonCustomer.customerId },
                    });

                    if (customer && customer.detail && customer.title && customer.type) {
                        throw new Error(customer.detail);
                    }

                    if (customer) {
                        await mergeBasketToCustomer(customer, isNewlyRegistered);
                        setCustomer(customer);
                    }

                    if (customer?.c_isStorePersonnel) {
                        await api.auth?.requestInstoreAgentToken();
                    }
                }
            },

            async acceptSoMeLogin(usid: string, code: string) {
                if (!usid || !code || !api.auth) {
                    throw new Error('Missing required parameters');
                }
                const skeletonCustomer = await api.auth.acceptSoMeLogin(usid, code);

                const customer = await api.shopperCustomers?.getCustomer({
                    parameters: { customerId: skeletonCustomer.customerId },
                });

                if (customer && customer.detail && customer.title && customer.type) {
                    throw new Error(customer.detail);
                }

                if (customer) {
                    await mergeBasketToCustomer(customer);
                    setCustomer(customer);
                    return customer;
                } else {
                    throw new Error('Missing customer data');
                }
            },

            async loginFacebook() {
                let authUrl;
                try {
                    authUrl = await api.auth?.loginFacebook();
                    authenticationEvent('Login (success)', location.pathname);
                } catch {
                    authenticationEvent('Login (error)', location.pathname);
                }
                return authUrl;
            },

            async loginGoogle() {
                let authUrl;
                try {
                    authUrl = await api.auth?.loginGoogle();
                    authenticationEvent('Login (success)', location.pathname);
                } catch {
                    authenticationEvent('Login (error)', location.pathname);
                }
                return authUrl;
            },

            /**
             * Log out current customer.
             * and retrive a guest access token
             */
            async logout() {
                try {
                    const customer = await api.auth?.logout();
                    setCustomer(customer);
                    authenticationEvent('Logout (success)', location.pathname);
                } catch {
                    authenticationEvent('Logout (error)', location.pathname);
                }
            },

            /**
             * Fetch current customer information.
             */
            async getCustomer() {
                if (!customer?.customerId) {
                    return null;
                }

                const fetchedCustomer = await api.shopperCustomers?.getCustomer({
                    parameters: { customerId: customer.customerId },
                });

                if (customer && customer.detail && customer.title && customer.type) {
                    throw new Error(customer.detail);
                }

                if (fetchedCustomer) {
                    setCustomer(fetchedCustomer);
                }
            },

            /**
             * Register a new customer account.
             *
             * @param {object} data
             * @param {boolean} data.acceptsMarketing - A boolean indicates whether customer accept marketing emails.
             * @param {string} data.email
             * @param {string} data.firstName
             * @param {string} data.lastName
             * @param {string} data.password
             */
            async registerCustomer(data: CustomerRegistration) {
                const body = {
                    customer: {
                        firstName: data.customer.firstName,
                        lastName: data.customer.lastName,
                        email: data.customer.email,
                        login: data.customer.email,
                        gender: data.customer.gender,
                        phoneMobile: data.customer.phone,
                        birthday: data.customer.birthday,
                        c_acceptedTermsAndConditions: data.customer.c_acceptedTermsAndConditions,
                        c_completeRegistration: data.customer.c_completeRegistration,
                        c_newsletter: data.customer.c_newsletter,
                        c_socialMedia: data.customer.c_socialMedia,
                        addresses: data.addresses,
                    },
                    password: data.password ?? null,
                };

                const response = await api.shopperCustomers?.registerCustomer({ body });

                // Check for error json response
                if (response?.detail && response?.title && response.type) {
                    throw new Error(response.detail);
                }

                // Send a new login request with the given credentials to ensure tokens are updated.
                if (body.customer.email && body.password) {
                    await self.login({ email: body.customer.email, password: body.password }, true);
                }
            },

            /**
             * Update customer information.
             *
             * @param {object} data
             * @param {string} data.email
             * @param {string} data.firstName
             * @param {string} data.lastName
             * @param {string} data.phone
             */
            async updateCustomer(data: Customer) {
                if (!customer?.customerId) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    throw new Error();
                }
                let body = {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phoneMobile: data.phone,
                    gender: data.gender,
                    birthday: data.birthday,

                    addresses: data.addresses,

                    c_acceptedTermsAndConditions: data.c_acceptedTermsAndConditions,
                    c_completeRegistration: data.c_completeRegistration,
                    c_newsletter: data.c_newsletter,
                    c_socialMedia: data.c_socialMedia,
                    email: customer.email,
                    login: customer.email,
                };

                if (!customer?.c_externallyAuthenticated) {
                    // NOTE/ISSUE
                    // The sdk is allowing you to change your email to an already-existing email.
                    // I would expect an error. We also want to keep the email and login the same
                    // for the customer, but the sdk isn't changing the login when we submit an
                    // updated email. This will lead to issues where you change your email but end
                    // up not being able to login since 'login' will no longer match the email.
                    body = {
                        ...body,
                        email: data.email ?? '',
                        login: data.email,
                    };
                }
                const response = await api.shopperCustomers?.updateCustomer({
                    body,
                    parameters: { customerId: customer.customerId },
                });

                // Check for error json response
                if (response && response.detail && response.title && response.type) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    throw new Error(response.detail);
                }
                // This previous request does return the updated customer profile, however it does
                // not include the 'entire' customer. It is missing address and payment methods.
                // We need to refetch the customer to make sure everything is up to date.
                await self.getCustomer();

                authenticationEvent('Edit user (success)', location.pathname);
            },
            /**
             * Update customer email.
             *
             * @param {string} data.email
             */
            async updateCustomerEmail(email: string) {
                if (!customer?.customerId) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    throw new Error();
                }

                const body = {
                    email,
                };

                const response = await api.shopperCustomers?.updateCustomer({
                    body,
                    parameters: { customerId: customer.customerId },
                });

                // Check for error json response
                if (response && response.detail && response.title && response.type) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    throw new Error(response.detail);
                }

                // This previous request does return the updated customer profile, however it does
                // not include the 'entire' customer. It is missing address and payment methods.
                // We need to refetch the customer to make sure everything is up to date.
                await self.getCustomer();

                authenticationEvent('Edit user (success)', location.pathname);
            },

            /**
             * Update customer password.
             *
             * @param {object} data
             * @param {string} data.currentPassword - The old password.
             * @param {string} data.password - The new password.
             */
            async updatePassword(data: { currentPassword: string; password: string }) {
                if (!customer?.customerId) {
                    authenticationEvent('Change password (error)', location.pathname);
                    throw new Error();
                }
                const body = {
                    password: data.password,
                    currentPassword: data.currentPassword,
                };

                // Note that we're using the raw response here. This request does not return
                // data when successful, but the sdk tries to parse the json (potential sdk bug).
                const rawResponse = await api.shopperCustomers?.updateCustomerPassword(
                    {
                        body,
                        parameters: { customerId: customer.customerId },
                    },
                    true
                );

                // Success has no json response to parse, but errors do, so handle that here.
                if (rawResponse && rawResponse.status >= 400) {
                    authenticationEvent('Change password (error)', location.pathname);
                    const json = await rawResponse.json();
                    // Check for error json response
                    if (json.detail && json.title && json.type) {
                        throw new Error(json.detail);
                    }
                }
                authenticationEvent('Change password (success)', location.pathname);
            },

            /**
             * @todo - Backend set up required
             * @param {string} login - customer email address
             */
            async requestResetPassword(login: string) {
                await api.auth?.requestResetPassword(login);
            },

            async setPasswordFromToken(email: string, token: string, password: string) {
                try {
                    await api.auth?.setPasswordFromToken(email, token, password);
                    authenticationEvent('Change password (success)', location.pathname);
                } catch {
                    authenticationEvent('Change password (error)', location.pathname);
                }
            },

            /**
             * Add a new saved address.
             *
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
             */
            async addSavedAddress(address: CustomerAddress) {
                if (!customer?.customerId) throw new Error();
                // The `addressId` field is required to save the customer's address to their account.
                // Rather than make the customer provide a name/id, we can generate a unique id behind
                // the scenes instead. This is only useful if you are not displaying the addr name/id
                // in the UI, which we aren't.
                const body = {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    addressId: nanoid(),
                    ...address,
                };

                await api.shopperCustomers?.createCustomerAddress({
                    body,
                    parameters: { customerId: customer.customerId },
                });

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer();
            },

            /**
             * Update a saved address.
             *
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
             */
            async updateSavedAddress(address: CustomerAddress) {
                if (!customer?.customerId) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    throw new Error('Edit user (error)');
                }
                const body = address;

                const rawResponse = await api.shopperCustomers?.updateCustomerAddress(
                    {
                        body,
                        parameters: {
                            customerId: customer.customerId,
                            addressName: address.addressId,
                        },
                    },
                    true
                );

                // Success has no json response to parse, but errors do, so handle that here.
                if (rawResponse && rawResponse.status >= 400) {
                    authenticationEvent('Edit user (error)', location.pathname);
                    const json = await rawResponse.json();
                    // Check for error json response
                    if (json.detail && json.title && json.type) {
                        throw new Error(json.detail);
                    }
                }

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer();
                authenticationEvent('Edit user (success)', location.pathname);
            },

            /**
             * Add a new saved payment instrument.
             *
             * @external PaymentInstrument
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customerpaymentinstrumentrequest
             */
            async addSavedPaymentInstrument(paymentInstrument: CustomerPaymentInstrumentRequest) {
                if (!customer?.customerId) throw new Error();
                const body = {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    bankRoutingNumber: '',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    giftCertificateCode: '',
                    ...paymentInstrument,
                    paymentCard: {
                        ...paymentInstrument.paymentCard,
                        securityCode: undefined,
                    },
                };
                await api.shopperCustomers?.createCustomerPaymentInstrument({
                    body,
                    parameters: { customerId: customer.customerId },
                });

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer();
            },

            /**
             * Remove a saved payment instrument.
             *
             * @param {string} paymentInstrumentId - The id of the payment payment instrument.
             */
            async removeSavedPaymentInstrument(paymentInstrumentId: string) {
                if (!customer?.customerId) throw new Error();
                // This SDK method must be called with `true` as second argument to avoid an error in
                // the sdk where it tries parsing json from an empty http response.
                await api.shopperCustomers?.deleteCustomerPaymentInstrument(
                    {
                        parameters: { customerId: customer.customerId, paymentInstrumentId },
                    },
                    true
                );

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer();
            },

            /**
             * Remove a saved address.
             *
             * @param {string} addressId - The id of the saved address.
             */
            async removeSavedAddress(addressId: string) {
                if (!customer?.customerId) throw new Error();
                // This SDK method must be called with `true` as second argument to avoid an error in
                // the sdk where it tries parsing json from an empty http response.
                await api.shopperCustomers?.removeCustomerAddress(
                    {
                        parameters: { customerId: customer.customerId, addressName: addressId },
                    },
                    true
                );

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer();
            },

            async getCustomerOrders(params: {
                organizationId: string;
                crossSites?: boolean;
                from?: string;
                until?: string;
                status?: string;
                siteId: string;
            }) {
                if (!customer?.customerId) throw new Error();
                const response = await api.shopperCustomers?.getCustomerOrders({
                    parameters: {
                        customerId: customer.customerId,
                        offset: 0,
                        limit: 10,
                        ...params,
                    },
                });
                return response;
            },

            async getOrder(orderNo: string) {
                const response = await api.shopperOrders?.getOrder({
                    parameters: { orderNo },
                });
                return response;
            },

            async getCustomerOrderProductsDetail(ids: string[]) {
                const response = await api.shopperProducts?.getProducts({
                    parameters: { ids: ids.join(',') },
                });

                const productMap = response?.data.reduce(
                    (result: { [x: string]: any }, item: { id: any }): { [x: string]: any } => {
                        const key = item.id;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        //@ts-ignore
                        result[key] = item;
                        return result;
                    },
                    {}
                );

                return productMap;
            },
        };
    }, [customer]);

    return self;
}
